/* eslint-disable quotes */
import useLocalStorage from "@/hooks/useLocalStorage";
import useTraces from "@/hooks/useTraces";
import useMapMode from "@/services/store/useMapMode";
import { nowWithSeconds } from "@/utils/functions/date";
import { angle } from "@/utils/trace/trace-utils";
import BottomLeftPanel from "@components/BottomLeftPanel";
import Player from "@components/Player";
import TopLeftPanel from "@components/TopLeftPanel";
import TraceEdit from "@components/TraceEdit";
import { Box, CircularProgress } from "@mui/material";
import { LatLng, LatLngTuple } from "leaflet";
import "leaflet-area-select";
import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import { MapContainer as LeafletMapContainer, TileLayer, useMap } from "react-leaflet";
import { AreaSelect, GeoJsonFiles, TraceLayer } from "./components";
import useOfflineTiles from "./useOfflineTiles";

const MapContainer = () => {
  const [zoom, setZoom] = useLocalStorage<number>("zoom", 13);
  const [center, setCenter] = useLocalStorage<LatLng>("center", new LatLng(48.808193, 2.12667));
  const [traceEditEnabled, setTraceEditEnabled] = useState(false);
  const [editedTracePoints, setEditedTracePoints] = useState<LatLngTuple[]>([]);
  const [satelitteViewToggled, setSatelitteViewToggled] = useState(false);

  const { createTrace: saveTrace } = useTraces();

  // Trace edit handling
  const onTraceEdit = (point: LatLngTuple) => {
    setEditedTracePoints([...editedTracePoints, point]);
  };

  const traceEditSave = () => {
    const points = editedTracePoints.map(
      (p, i): Point => ({ id: 0, x: p[0], y: p[1], z: 0, r: 0, timestamp: nowWithSeconds(i) })
    );
    points.forEach((_, index) => {
      if (index > 0) points[index].r = angle(points[index - 1], points[index]);
    });

    saveTrace({ id: 0, beginDate: new Date().getTime(), isActive: false, points: points, userId: null });
    setEditedTracePoints([]);
    setTraceEditEnabled(false);
  };

  const traceEditCancel = () => {
    setTraceEditEnabled(false);
    setEditedTracePoints([]);
  };

  const onToggleSatelliteView = (value: boolean) => {
    setSatelitteViewToggled(value);
  };

  return (
    <Box
      sx={{
        height: "100%",
        "& .leaflet-container": {
          zIndex: 0,
          width: "100%",
          height: "100%",
          cursor: traceEditEnabled ? "pointer" : "grab",
        },
        "& .leaflet-popup-tip": {
          display: "none",
        },
        "& .leaflet-popup": {
          left: "30px !important",
          bottom: "0px !important",
          marginBottom: "-75px !important",
        },
        "& .leaflet-popup-content": {
          margin: "unset",
          borderRadius: "4px !important",
          border: "1px solid",
        },
        "& .leaflet-popup-content p": {
          margin: "unset",
        },
        "& .leaflet-popup-content-wrapper": {
          borderRadius: "0px !important",
          background: "transparent",
          boxShadow: "none",
        },
      }}>
      <LeafletMapContainer
        id="map"
        center={center}
        zoom={zoom}
        maxZoom={21}
        zoomControl={false}
        doubleClickZoom={false}>
        <MapChild setZoom={setZoom} setCenter={setCenter} />
        {satelitteViewToggled && (
          <TileLayer
            url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
            minZoom={6}
            maxZoom={21}
            subdomains={["mt1", "mt2", "mt3"]}
          />
        )}
        <TraceLayer selectable={!traceEditEnabled} />
        <TopLeftPanel
          onTraceEdit={setTraceEditEnabled}
          onTraceEditSave={traceEditSave}
          onTraceEditCancel={traceEditCancel}
          onToggleSatelliteView={onToggleSatelliteView}
        />
        <AreaSelect />
        <BottomLeftPanel />
        <Player />
        {traceEditEnabled ? <TraceEdit onDraw={onTraceEdit} /> : null}

        <GeoJsonFiles />
      </LeafletMapContainer>
    </Box>
  );
};

interface IMapChild {
  // eslint-disable-next-line no-unused-vars
  setZoom: (newZoom: number) => void;
  // eslint-disable-next-line no-unused-vars
  setCenter: (center: LatLng) => void;
}

const MapChild: React.FC<IMapChild> = ({ setZoom, setCenter }) => {
  const map = useMap();
  const loading = useOfflineTiles();

  const setIsMapDragging = useMapMode((state) => state.setIsMapDragging);
  const setMap = useMapMode((state) => state.setMap);

  const handleMapChange = () => {
    setZoom(map.getZoom());
    setCenter(map.getCenter());
  };

  const handleMapDragStart = () => setIsMapDragging(true);

  const handleMapDragStop = () => {
    setTimeout(() => {
      setIsMapDragging(false);
    }, 150);
  };

  useEffect(() => {
    setMap(map);
    map.on("moveend", handleMapChange);
    map.on("dragstart", handleMapDragStart);
    map.on("dragend", handleMapDragStop);

    return () => {
      map.off("moveend", handleMapChange);
    };
  }, [map]);

  if (loading)
    return (
      <Box sx={{ position: "absolute", zIndex: 99999, top: "160px", left: 10 }}>
        <CircularProgress size="small" />
      </Box>
    );

  return null;
};

export default MapContainer;
